import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { makeTheme, type ColorMode } from '../theme.js';

interface ColorModeCtx {
  mode: ColorMode;
  toggle: () => void;
}
const Ctx = createContext<ColorModeCtx>({ mode: 'light', toggle: () => {} });

const KEY = 'ro_theme';

/** Provides a light/dark toggle (light by default, persisted) around MUI's ThemeProvider. */
export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ColorMode>(
    () => (localStorage.getItem(KEY) as ColorMode) || 'light',
  );
  const theme = useMemo(() => makeTheme(mode), [mode]);
  const value = useMemo<ColorModeCtx>(
    () => ({
      mode,
      toggle: () =>
        setMode((m) => {
          const next = m === 'light' ? 'dark' : 'light';
          localStorage.setItem(KEY, next);
          return next;
        }),
    }),
    [mode],
  );

  return (
    <Ctx.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </Ctx.Provider>
  );
}

export const useColorMode = () => useContext(Ctx);
